import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Building2, MapPin } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { AnimateIn } from '@/components/motion/AnimateIn'
import type { City } from '@/types/home'
import { OptimizedImage } from '@/components/ui/OptimizedImage'
import { cityFallbackImage } from '@/lib/cityFallbacks'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { cn } from '@/lib/utils'

const FALLBACK_GRADIENTS = [
  'from-primary-600 to-primary-900',
  'from-primary-500 to-qbc-navy',
  'from-secondary-500 to-primary-800',
  'from-primary-700 to-neutral-900',
  'from-primary-400 to-primary-700',
]

function resolveCitySrc(city: City, index: number): string {
  const fallback = cityFallbackImage(city.slug, index)
  return city.image && city.image !== fallback ? city.image : fallback
}

function CityCard({ city, index = 0 }: { city: City; index?: number }) {
  const fallback = cityFallbackImage(city.slug, index)
  const resolved = resolveCitySrc(city, index)
  const [src, setSrc] = useState(resolved)
  const [imgError, setImgError] = useState(false)
  const gradient = FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length]

  // Homepage session/API cache can paint an old fallback first; sync when live photo arrives.
  useEffect(() => {
    setSrc(resolved)
    setImgError(false)
  }, [resolved])

  const handleError = () => {
    if (src !== fallback) {
      setSrc(fallback)
      return
    }
    setImgError(true)
  }

  return (
    <Link
      to={buildPropertyHref('', { city: city.slug })}
      className="group relative block aspect-4/3 overflow-hidden rounded-2xl bg-neutral-100 shadow-sm ring-1 ring-neutral-200/80 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-200"
    >
      {!imgError ? (
        <OptimizedImage
          key={src}
          src={src}
          alt={city.name}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          priority={false}
          onError={handleError}
        />
      ) : (
        <div className={cn('absolute inset-0 bg-linear-to-br', gradient)} aria-hidden />
      )}

      <div className="absolute inset-0 bg-linear-to-t from-black/95 via-black/55 to-black/15" />

      <div className="absolute left-3 top-3 sm:left-3.5 sm:top-3.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm sm:text-xs">
          <MapPin className="h-3 w-3" />
          Gujarat
        </span>
      </div>

      <div className="absolute bottom-0 left-0 w-full p-3.5 sm:p-4">
        <h3 className="font-display text-base font-bold text-white drop-shadow-md sm:text-lg">
          {city.name}
        </h3>
        <p className="text-xs font-medium text-white/90 sm:text-sm">
          {city.propertyCount.toLocaleString()} properties
        </p>
        <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors group-hover:bg-primary-500 sm:text-xs">
          Explore <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  )
}

export function PopularCities({ cities }: { cities: City[] }) {
  return (
    <Container className="overflow-x-clip">
      <AnimateIn className="min-w-0">
        <Section
          title="Popular Cities"
          subtitle="Explore properties across top cities in Gujarat"
        >
          {cities.length > 0 ? (
            <>
              <CardSlider size="city" navVariant="side" hideScrollbar>
                {cities.slice(0, 10).map((city, i) => (
                  <CityCard key={`${city.id}-${city.image || city.slug}`} city={city} index={i} />
                ))}
              </CardSlider>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 py-16 text-center">
              <Building2 className="h-10 w-10 text-neutral-300" />
              <p className="mt-3 text-sm text-neutral-500">No cities available yet</p>
            </div>
          )}
        </Section>
      </AnimateIn>
    </Container>
  )
}
